/**
 * 플레어 스택·화염·복사열 도달범위를 옆에서 본 x-y 단면도로 그린다.
 * API 521 단순법(`flareRadiation.ts`)과 KOSHA Brzustowski-Sommer 법
 * (`koshaFlareRadiation.ts`) 둘 다 이 컴포넌트를 쓴다 — 화염 중심을 구하는
 * 방식만 다르고(`centerOffsetIsDirect` 로 전환), 이후 기하(점원 모델·목표
 * 지점이 항상 반지름 D 원 위에 온다)는 완전히 같다.
 *
 *   - 스택은 지면(y=0)에서 수직으로 높이 h 까지.
 *   - API 521: 화염은 스택 끝에서 바람 방향(+x)으로 (Δx, Δy) 만큼 기울어진
 *     직선이고, 화염 중심(점원)은 그 중점 (0.5Δx, h+0.5Δy) 이다(Figure C.2,
 *     사용자가 찾아준 공개 스프레드시트의 도해와도 일치 확인함).
 *   - KOSHA: 화염 중심이 스택 끝에서 (xc, yc) 만큼 바로 떨어진 위치다(끝점
 *     개념이 없음) — `centerOffsetIsDirect=true` 로 이 오프셋을 그대로 쓴다.
 *   - 복사열 도달범위는 화염 중심을 중심으로 한 반지름 D(목표 허용
 *     방사강도 K 에서 역산한 거리)의 원이다 — 점원 모델(K∝1/D²)이라
 *     이 원 위의 모든 점이 정확히 목표 방사강도 K 를 받는다.
 *   - 목표 지점(수평거리 r, 지면)은 이 원 위에 오도록 h 를 구한 것이므로
 *     항상 원과 지면의 교점에 그려진다. KOSHA 모델은 화염 바로 아래
 *     지점(r=xc)이 곧 그 지점이다(H=D−yc 식 자체가 이 가정에서 나온다).
 */
const REF_PX_HEIGHT = 640;

function fmt(v: number, digits = 1): string {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("ko-KR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function FlareGeometryDiagram({
  heightM,
  horizontalDisplacementM,
  verticalDisplacementM,
  radiationDistanceM,
  gradeDistanceM,
  windVelocityMs,
  allowableRadiationKwM2,
  /** true 면 horizontalDisplacementM·verticalDisplacementM 을 화염 "끝점"이
   * 아니라 화염 중심 오프셋 그 자체로 쓴다(KOSHA Brzustowski-Sommer 법처럼
   * 화염 끝점 개념이 없고 중심 위치(xc,yc)만 있는 모델용). 기본값 false는
   * API 521 단순법(화염길이·바람틸트로 구한 끝점의 중점이 화염 중심)과
   * 그대로 호환된다. */
  centerOffsetIsDirect = false,
  /** 목표지점 방사강도 표시 단위(기본 kW/m² — API 521). KOSHA 는 kcal/hr·m². */
  allowableRadiationUnit = "kW/m²",
  /** 화염 아래 표시할 오프셋 라벨. 생략하면 "Δx=…·Δy=…"(API 521 기본). */
  offsetLabel,
}: {
  heightM: number;
  horizontalDisplacementM: number;
  verticalDisplacementM: number;
  radiationDistanceM: number;
  gradeDistanceM: number;
  windVelocityMs: number;
  allowableRadiationKwM2: number;
  centerOffsetIsDirect?: boolean;
  allowableRadiationUnit?: string;
  offsetLabel?: string;
}) {
  const h = heightM;
  const dx = horizontalDisplacementM;
  const dy = verticalDisplacementM;
  const D = radiationDistanceM;
  const r = gradeDistanceM;

  const stackTip: [number, number] = [0, h];
  const flameCenter: [number, number] = centerOffsetIsDirect
    ? [dx, h + dy]
    : [0.5 * dx, h + 0.5 * dy];
  const flameEnd: [number, number] = centerOffsetIsDirect ? flameCenter : [dx, h + dy];
  const target: [number, number] = [r, 0];

  const worldXMinRaw = Math.min(0, flameCenter[0] - D, target[0]);
  const worldXMaxRaw = Math.max(dx, flameCenter[0] + D, target[0]);
  const worldYMinRaw = Math.min(0, flameCenter[1] - D);
  const worldYMaxRaw = Math.max(flameEnd[1], flameCenter[1] + D);

  const spanX = Math.max(worldXMaxRaw - worldXMinRaw, 1e-6);
  const spanY = Math.max(worldYMaxRaw - worldYMinRaw, 1e-6);
  const padX = 0.12 * spanX;
  const padTop = 0.18 * spanY; // 풍향 화살표·라벨 자리
  const padBottom = 0.1 * spanY;

  const worldXMin = worldXMinRaw - padX;
  const worldXMax = worldXMaxRaw + padX;
  const worldYMaxPadded = worldYMaxRaw + padTop;
  const worldYMinPadded = worldYMinRaw - padBottom;

  const Ww = worldXMax - worldXMin;
  const Hw = worldYMaxPadded - worldYMinPadded;
  const unitsPerPx = Hw / REF_PX_HEIGHT;

  const fontSm = 12 * unitsPerPx;
  const fontMd = 14 * unitsPerPx;
  const strokeThin = 1.2 * unitsPerPx;
  const strokeMed = 2 * unitsPerPx;
  const strokeThick = 4 * unitsPerPx;
  const dotR = 4 * unitsPerPx;
  const stackHalfWidth = Math.max(0.015 * Ww, 1.5 * unitsPerPx);

  const toY = (y: number) => worldYMaxPadded - y;

  const gradeY = toY(0);
  const stackTopY = toY(h);
  const flameEndSvg: [number, number] = [flameEnd[0], toY(flameEnd[1])];
  const flameCenterSvg: [number, number] = [flameCenter[0], toY(flameCenter[1])];
  const targetSvg: [number, number] = [target[0], toY(target[1])];

  const arrowX0 = worldXMin + 0.06 * Ww;
  const arrowX1 = arrowX0 + 0.14 * Ww;
  const arrowY = toY(worldYMaxRaw + padTop * 0.45);

  return (
    <div style={{ maxWidth: 720, margin: "12px 0" }}>
      <svg
        viewBox={`${worldXMin} 0 ${Ww} ${Hw}`}
        style={{ width: "100%", height: "auto", aspectRatio: `${Ww} / ${Hw}` }}
        role="img"
        aria-label="플레어 스택·화염·복사열 도달범위 개략도"
      >
        <defs>
          <radialGradient id="flrHeatGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ff6a3d" stopOpacity="0.35" />
            <stop offset="70%" stopColor="#ff6a3d" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#ff6a3d" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="flrFlame" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ffb648" />
            <stop offset="100%" stopColor="#e6432a" />
          </linearGradient>
          <marker id="flrWindArrowHead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="var(--text)" />
          </marker>
        </defs>

        {/* 복사열 도달범위(원) — 점원 모델이라 이 원 위 어디나 목표 K 를 받는다. */}
        <circle
          cx={flameCenter[0]}
          cy={flameCenterSvg[1]}
          r={D}
          fill="url(#flrHeatGlow)"
          stroke="#e6432a"
          strokeOpacity={0.6}
          strokeWidth={strokeThin}
          strokeDasharray={`${4 * unitsPerPx} ${3 * unitsPerPx}`}
        />

        {/* 지면 */}
        <line
          x1={worldXMin}
          y1={gradeY}
          x2={worldXMax}
          y2={gradeY}
          stroke="var(--border)"
          strokeWidth={strokeMed}
        />
        <text x={worldXMin + 0.01 * Ww} y={gradeY + fontSm * 1.4} fontSize={fontSm} fill="var(--muted)">
          지면
        </text>

        {/* 스택 */}
        <rect
          x={-stackHalfWidth}
          y={stackTopY}
          width={2 * stackHalfWidth}
          height={gradeY - stackTopY}
          fill="var(--muted)"
          fillOpacity={0.35}
          stroke="var(--text)"
          strokeWidth={strokeThin}
        />

        {/* 화염 */}
        <line
          x1={0}
          y1={stackTopY}
          x2={flameEndSvg[0]}
          y2={flameEndSvg[1]}
          stroke="url(#flrFlame)"
          strokeWidth={strokeThick}
          strokeLinecap="round"
        />

        {/* 화염 중심(점원) */}
        <circle cx={flameCenter[0]} cy={flameCenterSvg[1]} r={dotR} fill="#e6432a" />
        <text
          x={flameCenter[0] + dotR * 1.6}
          y={flameCenterSvg[1] - dotR}
          fontSize={fontSm}
          fill="var(--text)"
        >
          화염 중심
        </text>

        {/* 화염 중심 → 목표지점 (D) */}
        <line
          x1={flameCenter[0]}
          y1={flameCenterSvg[1]}
          x2={targetSvg[0]}
          y2={targetSvg[1]}
          stroke="var(--accent)"
          strokeWidth={strokeThin}
          strokeDasharray={`${3 * unitsPerPx} ${2 * unitsPerPx}`}
        />
        <text
          x={(flameCenter[0] + target[0]) / 2 + fontSm * 0.4}
          y={(flameCenterSvg[1] + targetSvg[1]) / 2}
          fontSize={fontSm}
          fill="var(--accent)"
        >
          D = {fmt(D)} m
        </text>

        {/* 목표지점 */}
        <circle cx={target[0]} cy={targetSvg[1]} r={dotR} fill="var(--accent)" />
        <text
          x={target[0] + dotR * 1.6}
          y={targetSvg[1] - dotR * 1.6}
          fontSize={fontSm}
          fill="var(--accent)"
        >
          목표지점 (K={fmt(allowableRadiationKwM2, 2)} {allowableRadiationUnit})
        </text>

        {/* r 치수선 (지면, 스택 밑동 ~ 목표지점) */}
        <line
          x1={0}
          y1={gradeY + fontSm * 2.2}
          x2={target[0]}
          y2={gradeY + fontSm * 2.2}
          stroke="var(--muted)"
          strokeWidth={strokeThin}
        />
        <text
          x={target[0] / 2}
          y={gradeY + fontSm * 3.6}
          fontSize={fontSm}
          fill="var(--muted)"
          textAnchor="middle"
        >
          r = {fmt(r)} m
        </text>

        {/* h 치수선 (스택 옆) */}
        <line
          x1={-stackHalfWidth - fontSm * 1.2}
          y1={gradeY}
          x2={-stackHalfWidth - fontSm * 1.2}
          y2={stackTopY}
          stroke="var(--muted)"
          strokeWidth={strokeThin}
        />
        <text
          x={-stackHalfWidth - fontSm * 1.6}
          y={(gradeY + stackTopY) / 2}
          fontSize={fontSm}
          fill="var(--muted)"
          textAnchor="end"
        >
          h = {fmt(h)} m
        </text>

        {/* 풍향 화살표 */}
        <line
          x1={arrowX0}
          y1={arrowY}
          x2={arrowX1}
          y2={arrowY}
          stroke="var(--text)"
          strokeWidth={strokeMed}
          markerEnd="url(#flrWindArrowHead)"
        />
        <text x={arrowX0} y={arrowY - fontSm * 0.8} fontSize={fontSm} fill="var(--text)">
          풍향 {fmt(windVelocityMs)} m/s
        </text>

        <text x={0} y={stackTopY - fontSm * 1.2} fontSize={fontSm} fill="var(--muted)" textAnchor="middle">
          {offsetLabel ?? `Δx=${fmt(dx)} m · Δy=${fmt(dy)} m`}
        </text>
      </svg>
      <p className="hint">
        점선 원은 화염 중심에서의 거리가 D인 지점 — 점원(point source) 모델이라
        원 위 어디서나 방사강도가 목표 K와 같습니다. 원 안쪽이 K를 넘는
        구역입니다.
      </p>
    </div>
  );
}
